import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getPersonalLoansByAdminIdAndRole, getAllCompanies,getAssignedBanks } from "../../../middleware/backend_hepler";
import { FaEdit } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faSpinner,
  faSearch,
  faTimesCircle,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../../../Context/AuthContext";
import DateFilterModal from "../../../components/DateFilterModal";
import EditPersonalViewModal from "./EditPersonalViewModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/Loader";
import Paginations from "../../../components/Paginations";


const PersonalLoanList = () => {
  const { adminName, adminRole, adminId } = useContext(AuthContext);
  const [personalLoans, setPersonalLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [messageDisplayed, setMessageDisplayed] = React.useState(false);
  const [approvedCount, setApprovedCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [companyLists, setCompanyLists] = useState([]);
  const [bankLists, setBankLists] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loansPerPage, setLoansPerPage] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (location.state?.successMessage && !messageDisplayed) {
      console.log(location.state.successMessage);
      toast.success(location.state.successMessage);
      setMessageDisplayed(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, messageDisplayed]);

  const handleStatusChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const isWithinDateRange = (dateString) => {
    if (!startDate && !endDate) return true;

    const loanDate = new Date(dateString);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start && end && start.toDateString() === end.toDateString()) {
      return loanDate.toDateString() === start.toDateString();
    }

    if (start && end) {
      return loanDate >= start && loanDate <= end;
    }
    if (start) {
      return loanDate >= start;
    }
    if (end) {
      return loanDate <= end;
    }

    return true;
  };

  useEffect(() => {
    const fetchPersonalLoans = async () => {
      try {
        const response = await getPersonalLoansByAdminIdAndRole();
        setPersonalLoans(response);
        setLoading(false);
      } catch (error) {
        setError("Error fetching personal loans.");
        setLoading(false);
      }
    };
    fetchPersonalLoans();
  }, []);

  let filteredPersonalLoans = [];

  if (adminRole === "admin") {
    filteredPersonalLoans = personalLoans;
  } else if (adminRole === "employee") {
    filteredPersonalLoans = personalLoans.filter((loan) => {
      const assignedToId = loan.assigned_to?.split(" - ")[0]?.replace("ID", "");
      const assignedToName = loan.assigned_to?.split(" - ")[1];

      return (
        loan.created_by === adminName ||
        loan.created_id === adminId ||
        assignedToId === adminId ||
        assignedToName === adminName
      );
    });
  } else if (adminRole === "customer") {
    filteredPersonalLoans = personalLoans.filter(
      (loan) => loan.created_id === adminId || loan.created_by === adminName
    );
  }

  const getBankNames = (bankListString, companyName) => {
    try {
      const bankIds = JSON.parse(bankListString);
      const bankNames = bankIds
        .map((bankId) => {
          const bank = bankLists.find((b) => b.bank_id === bankId);
          return bank ? bank.bank_name : null;
        })
        .filter((name) => name);
      return bankNames.length > 0 ? bankNames.join(", ") : companyName;
    } catch {
      return companyName;
    }
  };

  useEffect(() => {
    const fetchCompanyListsAndBanks = async () => {
      try {
        setLoading(true);

        // ---- existing company API ----
        const response = await getAllCompanies();
        const companies = response.data;
        setCompanyLists(companies);

        // ---- existing bank API ----
        if (companies.length > 0) {
          const allBankIds = [
            ...new Set(
              companies
                .map((c) => {
                  try {
                    const parsed = JSON.parse(c.bank_list);
                    return parsed ?? [];
                  } catch {
                    return [];
                  }
                })
                .flat()
            ),
          ];

          const bankResponse = await getAssignedBanks(allBankIds);
          setBankLists(bankResponse.data);
        }

        setLoading(false);
      } catch (error) {
        console.error(error);
        setError("Error fetching company or bank lists.");
        setLoading(false);
      }
    };

    fetchCompanyListsAndBanks();
  }, []);

  const filteredLoans = filteredPersonalLoans
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  .filter((loan) => {
    const searchQueryLower = searchQuery.toLowerCase();

    const company = companyLists.find(
      (c) => c.company_name === loan.company_name
    );
    const bankNames = company
      ? getBankNames(company.bank_list, loan.company_name)
      : loan.company_name;

    const matchesSearch =
      loan.name?.toLowerCase().includes(searchQueryLower) ||
      loan.form_id?.toString().toLowerCase().includes(searchQueryLower) ||
      loan.mobile?.toString().toLowerCase().includes(searchQueryLower) ||
      loan.created_by?.toString().toLowerCase().includes(searchQueryLower) ||
      bankNames.toLowerCase().includes(searchQueryLower);

    const matchesStatus =
      filterStatus === "All" || loan.status === filterStatus;

    const matchesDate = isWithinDateRange(loan.created_at);

    return matchesSearch && matchesStatus && matchesDate;
  });

  const indexOfLastLoan = currentPage * loansPerPage;
  const indexOfFirstLoan = indexOfLastLoan - loansPerPage;
  const currentLoans = filteredLoans.slice(indexOfFirstLoan, indexOfLastLoan);
  
  const statusClasses = {
    "In Process": "status-inprocess",
    "Open": "status-open",
    "Pending": "status-pending",
    "Approved": "status-approved",
    "Rejected": "status-rejected",
  };

  const handleCreateButtonClick = () => {
    navigate("/create-personal-loan");
  };

  const handleEdit = (form_id) => {
    navigate(`/edit-personal-loan/${form_id}`);
  };

  const handleRowClick = (form_id) => {
    setIsViewModalOpen(true);
    setSelectedLoanId(form_id);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handleCloseModal = () => {
    setIsViewModalOpen(false);
    setSelectedLoanId(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Add 5 hours and 30 minutes for IST
    date.setMinutes(date.getMinutes() + 330);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    let approved = 0,
      inProgress = 0,
      rejected = 0;

    if (adminRole === "employee") {
      approved = personalLoans.filter(
        (loan) =>
          (loan.created_id === adminId ||
            loan.created_by === adminId ||
            loan.assigned_to === adminId ||
            loan.assigned_to === adminName) &&
          loan.status === "Approved"
      ).length;

      inProgress = personalLoans.filter(
        (loan) =>
          (loan.created_id === adminId ||
            loan.created_by === adminId ||
            loan.assigned_to === adminId ||
            loan.assigned_to === adminName) &&
          loan.status === "In Process"
      ).length;

      rejected = personalLoans.filter(
        (loan) =>
          (loan.created_id === adminId ||
            loan.created_by === adminId ||
            loan.assigned_to === adminId ||
            loan.assigned_to === adminName) &&
          loan.status === "Rejected"
      ).length;
    } else if (adminRole === "admin") {
      approved = personalLoans.filter(
        (loan) => loan.status === "Approved"
      ).length;
      inProgress = personalLoans.filter(
        (loan) => loan.status === "In Process"
      ).length;
      rejected = personalLoans.filter(
        (loan) => loan.status === "Rejected"
      ).length;
    }

    setApprovedCount(approved);
    setInProgressCount(inProgress);
    setRejectedCount(rejected);
  }, [personalLoans, adminRole, adminName, adminId]);

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleApplyDateFilter = () => {
    setIsModalOpen(false);
  };

  const originalCounts = useMemo(() => {
    return {
      approved: personalLoans.filter((loan) => loan.status === "Approved")
        .length,
      inProgress: personalLoans.filter((loan) => loan.status === "In Process")
        .length,
      rejected: personalLoans.filter((loan) => loan.status === "Rejected")
        .length,
    };
  }, [personalLoans, adminRole, adminName]);

  if (loading) {
    return <Loader />;
  }
  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="home-loan-screen">
      <h4 className="agent-title">Personal Loan</h4>
      <div className="agent">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Id, Name, Mobile, AssignedTo, Bank"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
        </div>
        <button className="agent-button" onClick={handleCreateButtonClick}>
          <span className="plus-icon">+</span> 
          {adminRole === "customer" ? "Add New Loan" : "Add Loan"}
        </button>
      </div>
      {(adminRole !== "customer" &&
        <div className="dashboard-summary">
          <div className="summary-item">
            <div className="text-container">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="summary-icon-circle"
              />
              <h2>Approved</h2>
            </div>
            <p className="approve-count">{originalCounts.approved}</p>
          </div>
          <div className="summary-item">
            <div className="text-container">
              <FontAwesomeIcon
                icon={faSpinner}
                className="summary-icon-spinner"
              />
              <h2>In Process</h2>
            </div>
            <p className="inprogress-count">{originalCounts.inProgress}</p>
          </div>
          <div className="summary-item">
            <div className="text-container">
              <FontAwesomeIcon
                icon={faTimesCircle}
                className="summary-icon-time"
              />
              <h2>Rejected</h2>
            </div>
            <p className="reject-count">{originalCounts.rejected}</p>
          </div>
        </div>
      )}

      <div className="data-wrapper">
        <table className="data-grid">
          <thead>
            <tr>
              <th>Loan ID</th>
              <th>Application Details</th>
              <th>Loan Details</th>
              <th>Status
                <select
                  className="status-filter"
                  value={filterStatus}
                  onChange={handleStatusChange}
                >
                  <option value="All">All</option>
                  <option value="Open">Open</option>
                  <option value="In Process">In Process</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </th>
              <th>Date
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="apply-date-icon"
                  onClick={handleModalOpen}
                />
                <DateFilterModal
                  isOpen={isModalOpen}
                  onClose={handleModalClose}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  applyFilter={handleApplyDateFilter}
                />
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentLoans.length > 0 ? (
              currentLoans.map((loan) => {
                const company = companyLists.find(
                  (c) => c.company_name === loan.company_name
                );
                const bankNames = company
                  ? getBankNames(company.bank_list, loan.company_name)
                  : loan.company_name;

                return (
                  <tr
                    className="hover-row"
                    key={loan.form_id}
                    onClick={() => handleRowClick(loan.form_id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className="id-label label-tag label-tag-warning">
                        <span className="table-label">{loan.form_id}</span>
                      </span>
                      <br />
                    </td>
                    <td>
                      <span className="table-label">
                        Name: <span className="table-value">{loan.name}</span>
                      </span>
                      <br />
                      <span className="table-label">
                        Mobile:{" "}
                        <span className="table-value">{loan.mobile}</span>
                      </span>
                      <br />
                      <span className="table-label">
                        Email: <span className="table-value">{loan.email}</span>
                      </span>
                    </td>
                    <td>
                      <span className="table-label">
                        Company Name:{" "}
                        <span className="table-value">
                          {loan.company_name !== "Others"
                            ? loan.company_name
                            : loan.others_company}
                        </span>
                      </span>
                      <br />
                      <span className="table-label">
                        Assigned Bank:{" "}
                        <span className="table-value">{loan.bank_name}</span>
                      </span>
                      <br />
                      <span className="table-label">
                        Eligible Banks:{" "}
                        <span className="table-value">
                          {loan.company_name !== "Others"
                            ? bankNames
                            : "No Eligible Banks"}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className="table-label">
                        Status:{" "}
                        <span
                          className={`table-highlight-value ${
                            statusClasses[loan.status] || ""
                          }`}
                        >
                          {loan.status}
                        </span>
                      </span>
                      <br />
                      <span className="table-label">
                        Assigned To:{" "}
                        <span className="table-value">{loan.assigned_to}</span>
                      </span>
                      <br />
                      <span className="table-label">
                        Created By:{" "}
                        <span className="table-value">{loan.created_by}</span>
                      </span>
                    </td>
                    <td>
                      <span className="table-label">
                        Applied:{" "}
                        <span className="table-value">
                          {formatDate(loan.created_at)}
                        </span>
                      </span>
                      <br />
                    </td>
                    <td>
                      <FaEdit
                        className="edit-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(loan.form_id);
                        }}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: "center" }}>
                  No loans found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Paginations
          perPageData={loansPerPage}
          data={filteredLoans}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          isShowingPageLength={true}
          paginationDiv="pagination-container"
          paginationClass="pagination"
        />
      </div>

      {isViewModalOpen && selectedLoanId && (
        <EditPersonalViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          loanId={selectedLoanId}
        />
      )}
    </div>
  );
};

export default PersonalLoanList;
